import { useState, useCallback, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { useUserStore } from '@/stores/useUser.store';
import { getGuestId } from '@/utils/getGuestId';
import StepperHeader from './StepperHeader';
import AuthorizationStep from './steps/AuthorizationStep';
import ProjectLinkStep from './steps/ProjectLinkStep';
import GeneralDisclaimerStep from './steps/GeneralDisclaimerStep';
import AcknowledgementStep from './steps/AcknowledgementStep';
import FileUploadStep from './steps/FileUploadStep';
import BaseFeesPaymentStep from './steps/BaseFeesPaymentStep';
import SuccessStep from './steps/SuccessStep';

const { setUserRole } = useUserStore.getState();

// ─── Static config ────────────────────────────────────────────────────────────

const DEFAULT_STEP_DEFINITIONS = [
  { key: 'authorization', label: 'Authorization', order: 1 },
  { key: 'project_link', label: 'Project Link', order: 2 },
  { key: 'general_disclaimer', label: 'General Disclaimer', order: 3 },
  { key: 'acknowledgement', label: 'Acknowledgement', order: 4 },
  { key: 'file_upload', label: 'File Upload', order: 5 },
  { key: 'base_fees_payment', label: 'Base Fees Payment', order: 6 },
];

const STEP_COMPONENTS = {
  authorization: AuthorizationStep,
  project_link: ProjectLinkStep,
  general_disclaimer: GeneralDisclaimerStep,
  acknowledgement: AcknowledgementStep,
  file_upload: FileUploadStep,
  base_fees_payment: BaseFeesPaymentStep,
};

const STEP_META = {
  authorization: {
    title: 'Authorization Required',
    subtitle: 'For security purposes, please verify your identity to continue.',
  },
  project_link: {
    title: 'Link Your Project',
    subtitle: 'Connect this permit application to a project.',
  },
  general_disclaimer: {
    title: 'General Disclaimer',
    subtitle: 'Please read and accept the disclaimer to continue.',
  },
  acknowledgement: {
    title: 'Acknowledgements',
    subtitle: 'Please review and acknowledge all items below.',
  },
  file_upload: {
    title: 'Upload Documents',
    subtitle: 'Upload the required documents for your permit application.',
  },
  base_fees_payment: {
    title: 'Process Your Payment',
    subtitle: 'Please choose the payment method for the Zermit service fee to process and submit your application.',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Merge the always-present authorization step with whatever the backend returned.
 * If backendSteps is empty, all default steps are shown.
 * Steps with required !== true are skipped when backend data is provided.
 */
function buildSteps(backendSteps = []) {
  const authStepDef = DEFAULT_STEP_DEFINITIONS.find(s => s.key === 'authorization');

  if (!backendSteps.length) {
    return [...DEFAULT_STEP_DEFINITIONS].sort((a, b) => a.order - b.order);
  }

  const backendMap = new Map(backendSteps.map(s => [s.key, s]));

  // Merge the backend completed flag into the auth step as well.
  const authStep = backendMap.has('authorization')
    ? { ...authStepDef, ...backendMap.get('authorization') }
    : authStepDef;

  const merged = DEFAULT_STEP_DEFINITIONS.filter(s => s.key !== 'authorization' && backendMap.has(s.key))
    .map(s => ({ ...s, ...backendMap.get(s.key) }))
    .filter(s => s.required === true)
    .sort((a, b) => a.order - b.order);

  return [authStep, ...merged];
}

/** Re-shape message metadata into the submitPermit payload shape. */
function buildSubmitPayload(metadata = {}) {
  return {
    user_id: getGuestId(),
    application_id: null,
    scope_of_work: metadata.scope_of_work || null,
    jurisdiction_code: metadata.jurisdiction_code || null,
    portal_address: metadata.address || null,
    project_name: metadata.project_name || null,
    permit_type: metadata.permit_type || null,
    work_description: Array.isArray(metadata.work_description)
      ? metadata.work_description.join(', ')
      : metadata.work_description || null,
    unit: metadata.unit_number?.toString() || null,
    capacity_unit: metadata.ac_unit_count?.toString() || null,
    capacity: metadata.ac_capacity_in_hp || null,
    question_index: metadata.related_permit_type || 0,
    intent_state: metadata.status || null,
    metadata,
    submit_to_city: false,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PermitStepperModal — unified multi-step permit submission flow.
 *
 * Props:
 *  isOpen          – controls visibility
 *  onClose         – called when the user dismisses the modal
 *  permitChatId    – current permit chat ID
 *  projectId       – pre-linked project ID (optional; project_link step handles linking)
 *  messageMetadata – message.metadata from the chat bubble (for submitPermit payload)
 *  onComplete      – optional callback fired when the full flow finishes
 */
const PermitStepperModal = ({
  isOpen,
  onClose,
  permitChatId,
  projectId: initialProjectId = null,
  messageMetadata = {},
  onComplete,
}) => {
  const [fetchedSteps, setFetchedSteps] = useState(null);
  const [isFetchingSteps, setIsFetchingSteps] = useState(false);

  // Use API-fetched steps; fall back to defaults while loading or on error.
  const steps = buildSteps(fetchedSteps ?? []);

  const [currentIndex, setCurrentIndex] = useState(0);
  // Shared context accumulated across steps.
  const [context, setContext] = useState({
    projectId: initialProjectId,
    ackData: null,
    permitId: null,
    userId: null,
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset state and fetch stepper config whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;

    setContext({ projectId: initialProjectId, ackData: null, permitId: null, userId: null });
    setIsSuccess(false);
    setIsTransitioning(false);
    setCurrentIndex(0);
    setFetchedSteps(null);
    setIsFetchingSteps(true);

    appAxios
      .get('/PermitOnboardStepper/v1.0/get-permit-onboard-stepper', { params: { permitChatId } })
      .then(res => {
        const apiSteps = res.data?.data?.steps ?? res.data?.data ?? [];
        const normalized = Array.isArray(apiSteps) ? apiSteps : [];
        setFetchedSteps(normalized);

        // Advance past already-completed steps — rely solely on the completed flag.
        const builtSteps = buildSteps(normalized);
        const firstPending = builtSteps.findIndex(s => !s.completed);
        if (firstPending > 0) setCurrentIndex(firstPending);
      })
      .catch(() => setFetchedSteps([]))
      .finally(() => setIsFetchingSteps(false));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateContext = useCallback(updates => setContext(prev => ({ ...prev, ...updates })), []);

  // ── Mark stepper step complete on backend ──────────────────────────────────
  const updateStepperProgress = async stepKey => {
    try {
      await appAxios.post('/PermitOnboardStepper/v1.0/update-permit-onboard-stepper', {
        permitChatId,
        stepKey,
      });
    } catch {
      // Non-critical — don't block navigation
    }
  };

  // ── submitPermit wrapper ────────────────────────────────────────────────────
  const doSubmitPermit = async projectId => {
    try {
      const payload = buildSubmitPayload(messageMetadata);
      const res = await appAxios.post(`/Permit/v1.0/submitPermit/${projectId}/${permitChatId}`, payload);
      return {
        action: res.data?.ui_action,
        content: res.data?.content,
        status: res.data?.status,
        data: res.data?.data,
      };
    } catch (err) {
      const errData = err?.response?.data;
      return {
        action: errData?.ui_action,
        content: errData?.content,
        status: 'error',
        httpStatus: err?.response?.status,
        data: null,
      };
    }
  };

  // ── Between-step navigation helpers ────────────────────────────────────────
  const jumpToKey = useCallback(
    key => {
      const idx = steps.findIndex(s => s.key === key);
      if (idx >= 0) setCurrentIndex(idx);
    },
    [steps]
  );

  const advanceOrFinish = useCallback(
    fromIndex => {
      if (fromIndex >= steps.length - 1) {
        setIsSuccess(true);
      } else {
        setCurrentIndex(fromIndex + 1);
      }
    },
    [steps.length]
  );

  // ── Main step-complete handler (the "parent onSubmit") ──────────────────────
  const handleChildSubmit = async (stepData = {}) => {
    const completedKey = steps[currentIndex]?.key;
    const mergedContext = { ...context, ...stepData };
    updateContext(stepData);

    try {
      // ── Authorization ─────────────────────────────────────────────────────
      if (completedKey === 'authorization') {
        setIsTransitioning(true);
        await updateStepperProgress('authorization');
        try {
          const res = await appAxios.get('/Authorization/v1.0/getUserRole');
          const role = res.data?.data;
          if (role) setUserRole(role);
        } catch {
          // Non-critical — role will be re-fetched on next page load.
        }
        setIsTransitioning(false);
        advanceOrFinish(currentIndex);
        return;
      }

      // ── Project Link ──────────────────────────────────────────────────────
      // After the project is linked, call submitPermit to kick off server processing.
      if (completedKey === 'project_link') {
        setIsTransitioning(true);
        await updateStepperProgress('project_link');
        const result = await doSubmitPermit(mergedContext.projectId);

        if (result.action === 'show_acknowledgement' && result.content) {
          updateContext({ ackData: result.content });
          // Jump to general_disclaimer if it exists, otherwise acknowledgement.
          const target = steps.find(s => s.key === 'general_disclaimer') ? 'general_disclaimer' : 'acknowledgement';
          jumpToKey(target);
          setIsTransitioning(false);
          return;
        }

        if (result.action === 'show_file_upload_form') {
          jumpToKey('file_upload');
          setIsTransitioning(false);
          return;
        }

        if (result.status === 'Success' && result.data) {
          updateContext({ permitId: result.data._id, userId: result.data.user_id });
          jumpToKey('base_fees_payment');
          setIsTransitioning(false);
          return;
        }

        if (result.status === 'error' && result.httpStatus === 400) {
          // Already submitted — jump straight to payment or success.
          const payIdx = steps.findIndex(s => s.key === 'base_fees_payment');
          if (payIdx >= 0) setCurrentIndex(payIdx);
          else setIsSuccess(true);
          setIsTransitioning(false);
          return;
        }

        toast.error('Failed to process application. Please try again.');
        setIsTransitioning(false);
        return;
      }

      // ── File Upload ───────────────────────────────────────────────────────
      // After file upload, call submitPermit again to continue the flow.
      if (completedKey === 'file_upload') {
        setIsTransitioning(true);
        await updateStepperProgress('file_upload');
        const result = await doSubmitPermit(mergedContext.projectId);

        if (result.action === 'show_acknowledgement' && result.content) {
          updateContext({ ackData: result.content });
          const target = steps.find(s => s.key === 'general_disclaimer') ? 'general_disclaimer' : 'acknowledgement';
          jumpToKey(target);
          setIsTransitioning(false);
          return;
        }

        if (result.status === 'Success' && result.data) {
          updateContext({ permitId: result.data._id, userId: result.data.user_id });
          jumpToKey('base_fees_payment');
          setIsTransitioning(false);
          return;
        }

        if (result.status !== 'error') {
          // Unknown success-like response — advance normally.
          advanceOrFinish(currentIndex);
          setIsTransitioning(false);
          return;
        }

        toast.error('Failed to process application. Please try again.');
        setIsTransitioning(false);
        return;
      }

      // ── Acknowledgement ───────────────────────────────────────────────────
      // After the ack step's API call (change-acknowledgement-status) succeeds,
      // call submitPermit again to get the permitId for the payment step.
      if (completedKey === 'acknowledgement') {
        setIsTransitioning(true);
        await updateStepperProgress('acknowledgement');
        const result = await doSubmitPermit(mergedContext.projectId);

        if (result.status === 'Success' && result.data) {
          updateContext({ permitId: result.data._id, userId: result.data.user_id });
        } else if (result.status === 'error') {
          toast.error('Failed to finalize application. Please try again.');
          setIsTransitioning(false);
          return;
        }

        setIsTransitioning(false);
        advanceOrFinish(currentIndex);
        return;
      }

      // ── Default: just advance ─────────────────────────────────────────────
      await updateStepperProgress(completedKey);
      advanceOrFinish(currentIndex);
    } catch (err) {
      console.error('Stepper transition error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsTransitioning(false);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
  };

  const handleViewDashboard = () => {
    onComplete?.();
    onClose();
    window.location.href = '/citypermit/project';
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!isOpen) return null;

  if (isFetchingSteps) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] rounded-xl w-full max-w-[440px] p-6 shadow-2xl flex flex-col items-center gap-3 py-14">
          <Loader2 className="w-7 h-7 animate-spin text-[#FEBA08]" />
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  const currentStep = steps[currentIndex];
  const StepComponent = currentStep ? STEP_COMPONENTS[currentStep.key] : null;
  const meta = (currentStep && STEP_META[currentStep.key]) || { title: currentStep?.label ?? '', subtitle: '' };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] rounded-xl w-full max-w-[440px] max-h-[90vh] overflow-y-auto no-scrollbar p-6 shadow-2xl relative">
        {isSuccess ? (
          <SuccessStep onClose={onClose} onViewDashboard={handleViewDashboard} />
        ) : (
          <>
            <StepperHeader
              steps={steps}
              currentIndex={currentIndex}
              title={meta.title}
              subtitle={meta.subtitle}
              onClose={onClose}
            />

            {isTransitioning ? (
              <div className="flex flex-col items-center justify-center py-14 gap-3">
                <Loader2 className="w-7 h-7 animate-spin text-[#FEBA08]" />
                <p className="text-sm text-gray-400">Processing…</p>
              </div>
            ) : StepComponent ? (
              <StepComponent
                onSubmit={handleChildSubmit}
                onBack={handleBack}
                context={context}
                permitChatId={permitChatId}
                isFirst={currentIndex === 0}
              />
            ) : (
              <p className="text-center text-gray-400 py-8 text-sm">Step not configured.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PermitStepperModal;
